import { defineStore } from 'pinia'
import { router } from '@/router'
import axios from '../utils/apiClient'
import { User } from '@/models/User'
import { useCustomizerStore } from '@/stores/customizerStore'
import { useCompanyStore } from '@/stores/companyStore'
import { ElNotification } from 'element-plus'

interface AuthState {
    user: User | null
    returnUrl: string | null
    isLoading: boolean
}

export const useAuthStore = defineStore({
    id: 'auth',
    state: (): AuthState => ({
        user: null,
        returnUrl: null,
        isLoading: false,
    }),
    getters: {
        isAuthenticated: state => !!state.user,
        userRole: state => state.user?.role || null,
    },
    actions: {
        async initialize() {
            this.isLoading = true
            try {
                await this.restoreSession()
            } finally {
                this.isLoading = false
            }
        },

        async restoreSession() {
            const stored = localStorage.getItem('user')
            if (!stored) return

            try {
                const parsed = JSON.parse(stored)
                this.user = User.fromJSON(parsed)
                axios.defaults.headers.common['Authorization'] = `Bearer ${this.user.token}`
                await this.initializeCompanyAndTheme()
            } catch (err) {
                console.error('Session restoration failed:', err)
                this.clearSession()
            }
        },

        async initializeCompanyAndTheme() {
            const companyStore = useCompanyStore()
            const customizer = useCustomizerStore()

            if (this.user?.company) {
                companyStore.setListCompany([this.user.company])
                await companyStore.setCompanyID(this.user.company.id)
                customizer.setTheme(this.user.company.theme || 'ORANGE_THEME')
                return
            }

            if (companyStore.companies.length > 0) {
                await companyStore.setCompanyID(companyStore.companyID)
                return
            }

            // Fallback: fetch from configuration
            try {
                const res = await axios.get('configurations')
                if (res.data.company) companyStore.setListCompany(res.data.company)
                if (res.data.theme) customizer.setTheme(res.data.theme)
            } catch (err) {
                console.error('Failed to initialize configuration', err)
            }
        },

        async login(username: string, password: string) {
            this.isLoading = true
            try {
                const response = await axios.post<{ data: User }>('auth/login', { username, password })
                this.user = User.fromJSON(response.data)
                axios.defaults.headers.common['Authorization'] = `Bearer ${this.user.token}`

                // Persist session forever
                localStorage.setItem('user', JSON.stringify(response.data))

                await this.initializeCompanyAndTheme()
                await this.handlePostLogin()
            } catch (err) {
                this.clearSession()
                throw err
            } finally {
                this.isLoading = false
            }
        },

        async handlePostLogin() {
            if (!this.user) return

            if (this.user.isChangePassword) {
                await router.push('/auth/change-password')
                return
            }
            const redirectPath = this.returnUrl || this.user.homePage || this.getDefaultRoute() || '/'
            this.returnUrl = null
            await router.push(redirectPath)
        },

        getDefaultRoute(): string {
            if (!this.user) return '/auth/login'
            return this.user.homePage
            // switch (this.user.role) {
            //     case 'superAdmin': return '/dashboard'
            //     case 'admin': return '/admin/overview'
            //     default: return '/'
            // }
        },

        clearSession() {
            this.user = null
            delete axios.defaults.headers.common['Authorization']
            localStorage.removeItem('user')
        },

        async logout(showNotification = true) {
            this.clearSession()
            this.returnUrl = null
            if (showNotification) {
                ElNotification.success({
                    title: 'Logged out',
                    message: 'You have been successfully logged out',
                    showClose: false,
                })
            }
            await router.push('/auth/login')
        },
    },
})
